import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Reply, Smile, MoreVertical, Edit2, Trash2, 
  Check, CheckCheck, Download, File, Forward
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { formatMessageTime, getInitials } from '@/lib/utils';
import VoiceMessage from '@/components/VoiceMessage';

const EMOJI_LIST = ['👍', '❤️', '😂', '😮', '😢', '😡', '🎉', '🙏'];

const FormatText = ({ content, edited }) => {
  if (!content) return null;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = content.split(urlRegex);

  // 1. NEUE HILFSFUNKTION: Kürzt URLs für die Anzeige
  const shortenUrl = (url) => {
    // Wenn URL länger als 30 Zeichen ist, abschneiden und "..." anhängen
    if (url.length > 35) {
      return url.slice(0, 35) + '...';
    }
    return url;
  };
  
  return (
    <p className="px-4 py-2 whitespace-pre-wrap leading-relaxed break-words">
      {parts.map((part, i) => {
        if (part.match(urlRegex)) {
          return (
            <a 
              key={i} 
              href={part} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-blue-500 hover:underline break-all" 
              onClick={(e) => e.stopPropagation()}
            >
              {/* 2. Wir zeigen nur die gekürzte Version */}
              {shortenUrl(part)}
            </a>
          );
        }
        return part;
      })}
      {edited && (
        <span className="text-xs opacity-60 ml-2 select-none">(bearbeitet)</span>
      )}
    </p>
  );
};

export const MessageBubble = ({ 
  message, 
  isOwn, 
  showAvatar, 
  participantCount, 
  onReply, 
  onReact, 
  onEdit, 
  onDelete, 
  onForward, 
  currentUserId
}) => {
  const [showActions, setShowActions] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const BACKEND_URL = "https://lafabak.info";
  
  const isImage = message.type === 'image';
  const isVideo = message.type === 'video';
  const isVoice = message.type === 'voice' || message.type === 'audio';
  const isFile = message.type === 'file' || message.type === 'document';
  const isRead = message.read_by?.length >= participantCount;

  const msgTime = new Date(message.timestamp);
  const now = new Date();
  
  // --- HIER IST DIE NEUE LOGIK EINGEBAUT ---
  const timeLimit = 15 * 60 * 1000; // 15 Minuten
  
  // Darf bearbeiten? (Nur eigene + Zeitlimit + nur Text)
  const canEdit = isOwn && (now - msgTime) < timeLimit && message.type === 'text';

  // Darf für ALLE löschen? (Nur eigene + Zeitlimit)
  const canDeleteForEveryone = isOwn && (now - msgTime) < timeLimit;

  // Darf für MICH löschen? (IMMER erlaubt)
  const canDeleteForMe = true;
  // ----------------------------------------

  const mediaUrl = message.content?.startsWith('/api') 
    ? `${BACKEND_URL}${message.content}`
    : message.content;

  const groupedReactions = (message.reactions || []).reduce((acc, r) => {
    if (!acc[r.emoji]) acc[r.emoji] = [];
    acc[r.emoji].push(r);
    return acc;
  }, {});

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = mediaUrl;
    link.download = message.file_name || 'download';
    link.click();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      className={`group flex items-end gap-2 px-4 ${isOwn ? 'justify-end' : 'justify-start'}`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {showAvatar && !isOwn && (
        <Avatar className="h-8 w-8 shrink-0">
          <AvatarImage src={message.sender_avatar} />
          <AvatarFallback className="text-xs bg-slate-200 dark:bg-slate-700">
            {getInitials(message.sender_username)}
          </AvatarFallback>
        </Avatar>
      )}

      <div className={`max-w-[85%] sm:max-w-[70%] ${isOwn ? 'items-end' : 'items-start'} flex flex-col min-w-0`}>
        {showAvatar && !isOwn && (
          <span className="text-xs text-slate-500 dark:text-slate-400 mb-1 ml-1">
            {message.sender_username}
          </span>
        )}

        {message.forwarded && (
          <div className="text-xs mb-1 flex items-center gap-1 text-slate-500 dark:text-slate-400">
            <Forward className="w-3 h-3" />
            <span>Weitergeleitet{message.forwarded_from?.username ? ` von ${message.forwarded_from.username}` : ''}</span>
          </div>
        )}

        <div className="relative max-w-full">
          {/* Action Buttons - JETZT ÜBER DER BLASES */}
          <AnimatePresence>
            {(showActions || isMenuOpen) && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: 10 }}
                className={`absolute -top-9 ${isOwn ? 'right-0' : 'left-0'} flex items-center gap-1 z-10`}
              >
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 rounded-full bg-white dark:bg-slate-800 shadow border dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700"
                  onClick={() => onReply(message)}
                  title="Antworten"
                >
                  <Reply className="w-3.5 h-3.5 text-slate-600 dark:text-slate-300" />
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 rounded-full bg-white dark:bg-slate-800 shadow border dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700"
                  onClick={() => onForward && onForward(message)}
                  title="Weiterleiten"
                >
                  <Forward className="w-3.5 h-3.5 text-slate-600 dark:text-slate-300" />
                </Button>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 rounded-full bg-white dark:bg-slate-800 shadow border dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700"
                    >
                      <Smile className="w-3.5 h-3.5 text-slate-600 dark:text-slate-300" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-2" side="top">
                    <div className="flex gap-1">
                      {EMOJI_LIST.map(emoji => (
                        <button
                          key={emoji}
                          onClick={() => onReact(message.id, emoji)}
                          className="text-xl hover:scale-125 transition-transform p-1"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>

                {/* MENÜ: HIER WURDE DIE "isOwn"-ABFRAGE ENTFERNT UND DIE LOGIK EINGEBAUT */}
                <DropdownMenu onOpenChange={setIsMenuOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 rounded-full bg-white dark:bg-slate-800 shadow border dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700"
                    >
                      <MoreVertical className="w-3.5 h-3.5 text-slate-600 dark:text-slate-300" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    {/* Bearbeiten (Nur wenn erlaubt) */}
                    {canEdit && (
                      <DropdownMenuItem onClick={() => onEdit(message)}>
                        <Edit2 className="w-4 h-4 mr-2" />
                        Bearbeiten
                      </DropdownMenuItem>
                    )}
                    
                    {/* Für Alle löschen (Nur wenn erlaubt) */}
                    {canDeleteForEveryone && (
                      <DropdownMenuItem 
                        onClick={() => onDelete(message.id, true)}
                        className="text-red-500"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Für alle löschen
                      </DropdownMenuItem>
                    )}

                    {/* Für Mich löschen (Immer sichtbar) */}
                    <DropdownMenuItem onClick={() => onDelete(message.id, false)}>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Für mich löschen
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

              </motion.div>
            )}
          </AnimatePresence>

          {/* HAUPT-SPRECHBLASE */}
          <div className={`${isOwn ? 'chat-bubble-outgoing' : 'chat-bubble-incoming'} flex flex-col overflow-hidden max-w-full`}>
            
            {message.reply_to_message && (
              <div className={`text-xs mb-2 px-3 py-2 rounded-md border-l-4 ${
                isOwn 
                  ? 'bg-black/20 border-white/50 text-white' 
                  : 'bg-black/5 dark:bg-black/20 border-emerald-500 text-slate-700 dark:text-slate-200'
              }`}>
                <span className="font-bold block mb-0.5">{message.reply_to_message.sender_username}</span>
                <p className="truncate opacity-90 max-w-[200px]">
                  {message.reply_to_message.type !== 'text' 
                    ? `📎 ${message.reply_to_message.type}` 
                    : message.reply_to_message.content}
                </p>
              </div>
            )}

            {isVoice ? (
              <div className="p-2 min-w-[200px]">
                <VoiceMessage audioUrl={mediaUrl} isOwn={isOwn} />
              </div>
            ) : isImage ? (
              <div className="message-image">
                <img 
                  src={mediaUrl} 
                  alt="Shared" 
                  className="cursor-pointer rounded-lg max-h-[300px] w-auto object-cover"
                  onClick={() => window.open(mediaUrl, '_blank')}
                />
              </div>
            ) : isVideo ? (
              <div className="p-2">
                <video 
                  src={mediaUrl} 
                  controls 
                  className="max-w-full rounded-lg max-h-[300px]"
                />
              </div>
            ) : isFile ? (
              <div className="flex items-center gap-3 px-4 py-3">
                <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
                  <File className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{message.file_name || 'Datei'}</p>
                  <p className="text-xs opacity-70">Datei</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleDownload}
                  className="shrink-0"
                >
                  <Download className="w-5 h-5" />
                </Button>
              </div>
            ) : (
              <FormatText content={message.content} edited={message.edited} />
            )}
          </div>

          {/* Reaktionen */}
          {Object.keys(groupedReactions).length > 0 && (
            <div className={`flex flex-wrap gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
              {Object.entries(groupedReactions).map(([emoji, users]) => (
                <button
                  key={emoji}
                  onClick={() => onReact(message.id, emoji)}
                  className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
                    users.some(u => u.user_id === currentUserId)
                      ? 'bg-emerald-100 dark:bg-emerald-900/30 border border-emerald-500'
                      : 'bg-slate-100 dark:bg-slate-700'
                  }`}
                >
                  <span>{emoji}</span>
                  <span>{users.length}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Zeitstempel */}
        <div className={`flex items-center gap-1 mt-1 shrink-0 ${isOwn ? 'flex-row-reverse' : ''}`}>
          <span className="text-[10px] text-slate-400">
            {formatMessageTime(message.timestamp)}
          </span>
          {isOwn && (
            <span className={`read-receipt ${isRead ? 'read' : ''}`}>
              {isRead ? (
                <CheckCheck className="w-3 h-3" />
              ) : (
                <Check className="w-3 h-3" />
              )}
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default MessageBubble;